import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import RelationApi from 'web-app/api/relation-api';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {
    updateBasicChildInfoObservable,
    fetchBasicChildInfoObservable,
    updateKeyWorkersObservable,
    updateKikuChildObservable,
    fetchKikuChildObservable,
    fetchAutoGeneratedChildIdObservable,
    updateAutoGeneratedChildIdObservable,
} from 'web-app/react/entities/children/epics';

import * as Actions from './actions';

const fetch = action$ =>
    action$.ofType(Actions.fetch.type).mergeMap(({payload}) =>
        Observable.zip(
            fetchBasicChildInfoObservable(payload.childId),
            payload.withKiku ? fetchKikuChildObservable(payload.childId) : Observable.of({}),
            payload.showAutoGeneratedId ? fetchAutoGeneratedChildIdObservable(payload.childId) : Observable.of({}),
        )
            .flatMap(actions => [Actions.fetchSuccess.action(), ...actions.filter(a => a.type)])
            .catch(e => Observable.of(Actions.fetchFailed.action(e))),
    );

const updateBasicInfo = action$ =>
    action$.ofType(Actions.saveBasicInfo.type).mergeMap(({payload}) => {
        // Since the RealtionApi backend can't handle multiple calls at once, we have to do eventual calls sequentially
        const relationApiCalls = [
            ...(payload.livesWith
                ? Object.keys(payload.livesWith).map(
                      relationId => () => RelationApi.updateChildLivesAt(relationId, payload.livesWith[relationId]),
                  )
                : []),
            ...(payload.custody
                ? Object.keys(payload.custody).map(
                      relationId => () => RelationApi.updateChildCustody(relationId, payload.custody[relationId]),
                  )
                : []),
        ]
            .filter(call => call)
            .reduce((reduction, promise) => (reduction ? reduction.then(promise) : promise()), null);

        // Since the basic info tab is made up of multiple data-points, this observable ensures that data is only
        //  sent if the respective payload is included
        return Observable.zip(
            Observable.of(payload.childId),
            // We don't want to emit the updateBasicInfo success action at this point, since this api call only
            //   returns the child without relations, hence we need to fetch the basic info again after this is done
            payload.child
                ? updateBasicChildInfoObservable(payload.child.childId, payload.child).map(() => ({}))
                : Observable.of({}),

            payload.keyWorkers
                ? updateKeyWorkersObservable(
                      payload.childId,
                      payload.keyWorkers.primaryKeyWorker,
                      payload.keyWorkers.secondaryKeyWorker,
                  )
                : Observable.of({}),

            relationApiCalls ? Observable.fromPromiseDefer(() => relationApiCalls).map(() => ({})) : Observable.of({}),

            payload.kikuChild ? updateKikuChildObservable(payload.childId, payload.kikuChild) : Observable.of({}),

            payload.autoGeneratedId
                ? updateAutoGeneratedChildIdObservable(payload.childId, payload.autoGeneratedId)
                : Observable.of({}),
        )
            .mergeMap(([childId, ...successActions]) =>
                Observable.concat(
                    fetchBasicChildInfoObservable(childId),
                    ...successActions.filter(a => a.type).map(a => Observable.of(a)),
                    Observable.of(GlobalEventActions.updateSuccess.action(i18next.t('childProfile.profileUpdated'))),
                    Observable.of(Actions.saveBasicInfoSuccess.action()),
                ),
            )
            .catch(e => Observable.of(Actions.saveBasicInfoFailed.action(e)));
    });

export default combineEpics(fetch, updateBasicInfo);
